
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';

const PrivacyPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Update SEO meta tags
    updateMetaTags({
      title: `${t.privacy} | Future Level`,
      description: `${t.privacy} - Future Level - Informationen zum Datenschutz und zur Verarbeitung personenbezogener Daten`,
      keywords: "Datenschutz, Privacy Policy, DSGVO, Cookies, personenbezogene Daten, Future Level",
      canonicalUrl: getCanonicalUrl(currentLanguage === 'de' ? 'datenschutz' : 
                                   currentLanguage === 'en' ? 'privacy-policy' : 
                                   currentLanguage === 'fr' ? 'politique-de-confidentialite' : 'informativa-privacy', 
                                   currentLanguage),
      language: currentLanguage,
      ogType: "website"
    });
  }, [currentLanguage, t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8">{t.privacy}</h1>
            
            {/* Swiss-law compliant Privacy Policy content */}
            <div className="prose max-w-none">
              <p className="text-lg mb-4">
                Der Schutz Ihrer persönlichen Daten ist uns ein wichtiges Anliegen. In dieser Datenschutzerklärung 
                informieren wir Sie über die Verarbeitung personenbezogener Daten bei der Nutzung unserer Website 
                und unserer Dienstleistungen.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">1. Verantwortliche Stelle</h2>
              <p className="mb-4">
                Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br />
                Future Level GmbH<br />
                Musterstrasse 123<br />
                8000 Zürich<br />
                Schweiz<br />
                E-Mail: info@future-level.ch<br />
                Telefon: +41 44 123 45 67
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">2. Erhebung und Verarbeitung personenbezogener Daten</h2>
              <p className="mb-4">
                Wir erheben personenbezogene Daten, wenn Sie uns diese im Rahmen Ihrer Bestellung, bei einer Kontaktaufnahme 
                oder bei Registrierung für unseren Newsletter mitteilen. Welche Daten erhoben werden, ist aus den jeweiligen 
                Eingabeformularen ersichtlich. Wir verwenden die von Ihnen mitgeteilten Daten zur Vertragsabwicklung und 
                Bearbeitung Ihrer Anfragen.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">3. Verwendung von Cookies</h2>
              <p className="mb-4">
                Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf Ihrem Endgerät gespeichert werden 
                und die bestimmte Einstellungen und Daten zum Austausch mit unserem System über Ihren Browser speichern. 
                Einige der von uns verwendeten Cookies werden nach Ende der Browser-Sitzung, also nach Schließen Ihres 
                Browsers, wieder gelöscht (sog. Sitzungs-Cookies). Andere Cookies verbleiben auf Ihrem Endgerät und 
                ermöglichen uns, Ihren Browser beim nächsten Besuch wiederzuerkennen (persistente Cookies).
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">4. Newsletter</h2>
              <p className="mb-4">
                Wenn Sie den auf der Website angebotenen Newsletter beziehen möchten, benötigen wir von Ihnen eine 
                E-Mail-Adresse sowie Informationen, welche uns die Überprüfung gestatten, dass Sie der Inhaber der 
                angegebenen E-Mail-Adresse sind und mit dem Empfang des Newsletters einverstanden sind. Diese Daten 
                verwenden wir ausschließlich für den Versand der angeforderten Informationen und geben diese nicht 
                an Dritte weiter. Die erteilte Einwilligung zur Speicherung der Daten, der E-Mail-Adresse sowie deren 
                Nutzung zum Versand des Newsletters können Sie jederzeit widerrufen.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">5. Google Analytics</h2>
              <p className="mb-4">
                Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Inc. („Google"). Google 
                Analytics verwendet sog. „Cookies", Textdateien, die auf Ihrem Computer gespeichert werden und die 
                eine Analyse der Benutzung der Website durch Sie ermöglichen. Wir verarbeiten Ihre Daten auf Grundlage 
                unseres berechtigten Interesses an der wirtschaftlichen Führung unseres Unternehmens (Art. 6 Abs. 1 
                lit. f DSGVO). Sie können die Speicherung der Cookies durch eine entsprechende Einstellung Ihrer 
                Browser-Software verhindern.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">6. Ihre Rechte</h2>
              <p className="mb-4">
                Sie haben das Recht, jederzeit Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu 
                erhalten. Ebenso haben Sie das Recht auf Berichtigung, Sperrung oder, abgesehen von der 
                vorgeschriebenen Datenspeicherung zur Geschäftsabwicklung, Löschung Ihrer personenbezogenen Daten. 
                Sie können auch die Einschränkung der Verarbeitung verlangen, der Verarbeitung widersprechen oder 
                sich bei einer Aufsichtsbehörde beschweren.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">7. Datensicherheit</h2>
              <p className="mb-4">
                Wir sichern unsere Website und sonstigen Systeme durch technische und organisatorische Maßnahmen 
                gegen Verlust, Zerstörung, Zugriff, Veränderung oder Verbreitung Ihrer Daten durch unbefugte Personen.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">8. Änderungen dieser Datenschutzerklärung</h2>
              <p className="mb-4">
                Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit unter Beachtung der geltenden 
                Datenschutzvorschriften zu ändern.
              </p>
              
              <p className="mt-8 text-sm text-gray-500">
                Stand: April 2025
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
